import { Check, Trash } from '@phosphor-icons/react';
import { WEEKDAY_SHORT, type Timetable, type TimetableDay } from '@shh/shared';
import { Button, ChoiceGroup, Text, TextField } from '@shh/ui';
import { useState } from 'react';
import { deleteTimetable, saveTimetable } from '../data/api';
import { timetableName, type M } from '../data/derive';
import { useFamily } from '../data/family';
import { locale } from '../lib/locale';
import { settingsText } from '../i18n/settings';
import { TimetableView } from './TimetableView';

export type DayKey = '1' | '2' | '3' | '4' | '5';
export const DAYS: DayKey[] = ['1', '2', '3', '4', '5'];
const isTime = (v: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v);

/**
 * School / Kita times of one person (shown in the shared calendar). Since 1.28 also from the
 * ＋ menu ("Schicht- oder Stundenplan") and from the band in the calendar.
 */
export function TimetableForm({ member, onDone }: { member: M; onDone: () => void }) {
  const f = useFamily();
  const existing = f.timetables[member.id];
  const [label, setLabel] = useState(
    existing?.label ?? (member.ageProfile === 'picture' ? 'Kita' : 'Schule'),
  );
  const [days, setDays] = useState<
    Partial<Record<DayKey, { start: string; end: string; lessons: string }>>
  >(() =>
    Object.fromEntries(
      DAYS.map((d) => {
        const day = existing?.days[d];
        return [
          d,
          day
            ? { start: day.start, end: day.end, lessons: day.lessons.join(', ') }
            : existing
              ? undefined
              : { start: '08:00', end: '13:15', lessons: '' },
        ];
      }).filter(([, v]) => v),
    ),
  );
  const [busy, setBusy] = useState(false);
  const active = DAYS.filter((d) => days[d]);
  const valid =
    active.length > 0 &&
    active.every(
      (d) => isTime(days[d]!.start) && isTime(days[d]!.end) && days[d]!.start < days[d]!.end,
    );

  const set = (d: DayKey, patch: Partial<{ start: string; end: string; lessons: string }>) =>
    setDays((s) => ({ ...s, [d]: { ...s[d]!, ...patch } }));
  const toggle = (d: DayKey) =>
    setDays((s) => {
      const next = { ...s };
      if (next[d]) delete next[d];
      else next[d] = { ...(s[active[0] ?? '1'] ?? { start: '08:00', end: '13:15' }), lessons: '' };
      return next;
    });
  // Same times for every day – the common case, one tap.
  const copyFirst = () => {
    const first = days[active[0]!];
    if (!first) return;
    setDays((s) =>
      Object.fromEntries(active.map((d) => [d, { ...s[d]!, start: first.start, end: first.end }])),
    );
  };

  const timetable: Timetable = {
    memberId: member.id,
    label: label.trim() || 'Schule',
    days: Object.fromEntries(
      active.map((d) => [
        d,
        {
          start: days[d]!.start,
          end: days[d]!.end,
          lessons: days[d]!.lessons.split(',')
            .map((x) => x.trim())
            .filter(Boolean)
            .slice(0, 12),
        } satisfies TimetableDay,
      ]),
    ),
  };

  return (
    <form className="flex flex-col gap-gap-lg" onSubmit={(e) => e.preventDefault()}>
      <ChoiceGroup
        label={settingsText.what}
        value={label}
        onChange={setLabel}
        choices={['Schule', 'Kita', 'OGS'].map((v) => ({ value: v, label: timetableName(v) }))}
      />
      <fieldset className="flex flex-col gap-gap-sm">
        <legend className="mb-gap-xs text-sm font-semibold text-text-secondary">
          {settingsText.whichDays}
        </legend>
        <div className="flex flex-wrap gap-gap-xs">
          {DAYS.map((d) => (
            <Button
              key={d}
              variant={days[d] ? 'primary' : 'secondary'}
              aria-pressed={!!days[d]}
              onPress={() => toggle(d)}
            >
              {WEEKDAY_SHORT[locale][Number(d) - 1]}
            </Button>
          ))}
        </div>
      </fieldset>
      {active.map((d, i) => (
        <fieldset
          key={d}
          className="flex flex-col gap-gap-sm rounded-control bg-surface-sunken p-inset-sm"
        >
          <legend className="sr-only">{WEEKDAY_SHORT[locale][Number(d) - 1]}</legend>
          <div className="grid grid-cols-[3rem_1fr_1fr] items-end gap-gap-sm">
            <span aria-hidden className="pb-inset-xs font-display text-lg font-black">
              {WEEKDAY_SHORT[locale][Number(d) - 1]}
            </span>
            <TextField
              label={settingsText.start}
              type="time"
              value={days[d]!.start}
              onChange={(v) => set(d, { start: v })}
            />
            <TextField
              label={settingsText.end}
              type="time"
              value={days[d]!.end}
              onChange={(v) => set(d, { end: v })}
            />
          </div>
          <TextField
            label={settingsText.lessons}
            placeholder={settingsText.lessonsExample}
            value={days[d]!.lessons}
            onChange={(v) => set(d, { lessons: v })}
          />
          {i === 0 && active.length > 1 && (
            <Button variant="quiet" className="self-start" onPress={copyFirst}>
              {settingsText.sameTimes}
            </Button>
          )}
        </fieldset>
      ))}
      {valid && Object.keys(timetable.days).length > 0 && (
        <div className="flex flex-col gap-gap-xs">
          <Text size="sm" weight="semibold" tone="secondary">
            {settingsText.preview}
          </Text>
          <TimetableView timetable={timetable} today={f.today} />
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-gap-sm">
        {existing ? (
          <Button
            variant="quiet"
            icon={Trash}
            onPress={() => void deleteTimetable(f.householdId, member.id).then(onDone)}
          >
            {settingsText.deletePlan}
          </Button>
        ) : (
          <Button variant="secondary" onPress={onDone}>
            {settingsText.cancel}
          </Button>
        )}
        <Button
          size="lg"
          icon={Check}
          isDisabled={!valid || busy}
          onPress={() => {
            setBusy(true);
            void saveTimetable(f.householdId, timetable).then(onDone);
          }}
        >
          {settingsText.save}
        </Button>
      </div>
    </form>
  );
}
